<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import debounce from "lodash-es/debounce";
import { type ProductDetails, SalesMethod } from "mws-ts-v2-sdk";

import { queries } from "~/queries";

type Props = {
  product: ProductDetails;
  isProductDetailsV2: boolean;
  currentUserWon?: boolean;
};

const { currentUserWon = false, product } = defineProps<Props>();

const route = useRoute();

const productId = computed(() => product.id);
const productIdentifier = computed(
  () => route.params.productIdentifier as string,
);

const countdownDateInfo = computed(() => {
  if (product.salesMethod === SalesMethod.Bidding) {
    return {
      startDate: product.startDate,
      endDate: product.endDate,
      finished: product.finished,
      started: new Date() > product.startDate,
    };
  }

  return undefined;
});

const queryClient = useQueryClient();
const platformStore = usePlatformStore();
const platformId = computed(
  () => platformStore.getPlatformConfiguration.platformId,
);

const onRefetchProductDetails = () => {
  queryClient.invalidateQueries({
    queryKey: queries.products.details(productIdentifier, platformId).queryKey,
  });
  queryClient.invalidateQueries({
    queryKey: queries.products.bids(productId, platformId).queryKey,
  });
};

const debouncedRefetchProductDetails = debounce(
  (timeoutAmount: number = 0) =>
    setTimeout(onRefetchProductDetails, timeoutAmount),
  1000,
);
</script>

<template>
  <ProductCountdown
    v-if="product.salesMethod === SalesMethod.Bidding && countdownDateInfo"
    v-bind="countdownDateInfo"
    view="product"
    :current-user-won="currentUserWon"
    @product-finished="() => debouncedRefetchProductDetails(5000)"
    @product-running-out="debouncedRefetchProductDetails"
  />
</template>

<style lang="postcss" scoped>
.product-countdown {
  &__product {
    @apply flex flex-col gap-2 p-3 md:p-4;
  }

  &__account {
    @apply flex flex-col;
    @apply text-platform-text-2;
  }

  &__text {
    @apply font-semibold;
    &__coming-soon {
      @apply text-3xl;
    }
  }

  &__product-timer {
    @apply font-semibold text-2xl md:text-3xl;
    @apply text-platform-text-2;
  }

  &__finished-date {
    @apply font-normal text-sm space-x-1;
  }

  &__start-date {
    @apply font-normal text-xl;
  }

  &__winner-message {
    @apply font-semibold text-2xl md:text-3xl;
  }

  &__account-timer {
    @apply font-semibold text-xs md:text-sm;
  }
}
</style>
